import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useHaptics } from '../../utils/haptics'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import { accountApi, jobsApi } from '../../utils/accountApi'

const TRADE_TAGS = { Plumbing: 'plumbing', Electrical: 'electrical', HVAC: 'hvac' }
const ROOM_TAGS = ['Kitchen', 'Bathroom', 'Living Room']

export default function ServiceRequest() {
  const navigate = useNavigate()
  const location = useLocation()
  const haptics = useHaptics()
  const { showSuccess, showError } = useNotifications()
  
  // Get service details from navigation state
  const serviceDetails = location.state?.serviceDetails
  const fromServiceCard = location.state?.fromServiceCard
  const requiredExpertise = location.state?.requiredExpertise || []
  
  const [mode, setMode] = useState('issue')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  
  // Enhanced form fields for detailed service requests
  const [equipmentDetails, setEquipmentDetails] = useState({
    currentEquipment: '',
    brand: '',
    model: '',
    age: '',
    condition: ''
  })
  const [faultDescription, setFaultDescription] = useState('')
  const [hasReplacementUnit, setHasReplacementUnit] = useState(null) // null, true, false
  const [trade, setTrade] = useState(location.state?.trade || location.state?.serviceType || null)
  const [urgent, setUrgent] = useState(false)
  const [rooms, setRooms] = useState([])
  const [addresses, setAddresses] = useState([])
  const [addressId, setAddressId] = useState('')

  useEffect(() => {
    accountApi.addresses().then(list => {
      setAddresses(list)
      setAddressId((list.find(a => a.is_default) || list[0])?.id || '')
    }).catch(() => setAddresses([]))
  }, [])

  // Initialize form with service details if coming from service card
  useEffect(() => {
    if (fromServiceCard && serviceDetails) {
      setDescription(`Service Request: ${serviceDetails.title}\n\n${serviceDetails.description}`)
    }
  }, [fromServiceCard, serviceDetails])

  const handleRefresh = async () => {
    setAddresses(await accountApi.addresses())
  }

  const handleEquipmentChange = (field, value) => {
    setEquipmentDetails(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!description.trim()) {
      showError('Please describe the issue')
      return
    }

    setLoading(true)
    try {
      // Everything the customer filled in goes into the request description
      const eq = equipmentDetails
      const details = [
        `Type: ${mode}`,
        rooms.length ? `Location: ${rooms.join(', ')}` : null,
        eq.currentEquipment || eq.brand || eq.model ? `Equipment: ${[eq.currentEquipment, eq.brand, eq.model].filter(Boolean).join(' ')}` : null,
        eq.age ? `Age: ${eq.age} years` : null,
        eq.condition ? `Condition: ${eq.condition}` : null,
        faultDescription.trim() ? `Fault: ${faultDescription.trim()}` : null,
        hasReplacementUnit === true ? 'Customer has the replacement unit' : hasReplacementUnit === false ? 'Customer needs a new unit supplied' : null
      ].filter(Boolean)
      await jobsApi.createRequest({
        title: serviceDetails?.title,
        description: [description.trim(), '', ...details].join('\n').trim(),
        trade: trade || serviceDetails?.category || undefined,
        urgency: urgent ? 'HIGH' : 'MEDIUM',
        addressId: addressId || undefined
      })
      haptics.success()
      showSuccess('Service request submitted successfully!')
      navigate('/orders')
    } catch (error) {
      haptics.error()
      showError(error.message || 'Failed to submit request. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageLayout 
      title={fromServiceCard ? serviceDetails?.title || "Request Service" : "Request Service"}
      subtitle={fromServiceCard ? "Provide details for accurate quotes from qualified professionals" : "Describe your issue and we'll connect you with the right contractor"}
      onRefresh={handleRefresh}
      showBackButton={true}
    >
      <form onSubmit={handleSubmit} className="space-y-6" aria-label="Service request">
        {/* Request Type */}
        <Card>
          <h3 className="font-semibold text-slate-900 mb-3">Request Type</h3>
          <div className="flex gap-2 flex-wrap">
            {['issue', 'fault', 'inquiry', 'requirement'].map(type => (
              <button 
                key={type}
                type="button"
                onClick={() => setMode(type)} 
                className={`px-4 py-2 rounded-xl text-sm font-medium touch-manipulation transition-colors ${
                  mode === type 
                    ? 'bg-slate-900 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </Card>

        {/* Photo Upload - Enhanced */}
        <Card>
          <h3 className="font-semibold text-slate-900 mb-3">📷 Photos of Current Equipment</h3>
          <p className="text-sm text-slate-600 mb-4">Photos of the equipment, connections and any visible issues help professionals quote</p>
          
          <div className="relative" title="Not available yet">
            <div className="h-32 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center text-slate-400 opacity-60 cursor-not-allowed">
              <div className="text-center">
                <div className="text-2xl mb-2">📷</div>
                <p className="text-sm font-medium">Photo upload is not available yet</p>
                <p className="text-xs text-slate-400">Describe what you see below; use AI Inspect to share photos</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Equipment Details */}
        {fromServiceCard && (
          <Card>
            <h3 className="font-semibold text-slate-900 mb-3">🔧 Current Equipment Details</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Equipment Type</label>
                <input
                  type="text"
                  value={equipmentDetails.currentEquipment}
                  onChange={(e) => handleEquipmentChange('currentEquipment', e.target.value)}
                  placeholder="e.g., Electric Water Heater, Gas Furnace"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Brand</label>
                  <input
                    type="text"
                    value={equipmentDetails.brand}
                    onChange={(e) => handleEquipmentChange('brand', e.target.value)}
                    placeholder="e.g., Rheem, Bradford"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Model</label>
                  <input
                    type="text"
                    value={equipmentDetails.model}
                    onChange={(e) => handleEquipmentChange('model', e.target.value)}
                    placeholder="Model number"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Age</label>
                  <select
                    value={equipmentDetails.age}
                    onChange={(e) => handleEquipmentChange('age', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  >
                    <option value="">Select age</option>
                    <option value="0-2">0-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="11-15">11-15 years</option>
                    <option value="15+">15+ years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Condition</label>
                  <select
                    value={equipmentDetails.condition}
                    onChange={(e) => handleEquipmentChange('condition', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  >
                    <option value="">Select condition</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                    <option value="not-working">Not Working</option>
                  </select>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Fault Description */}
        {fromServiceCard && (
          <Card>
            <h3 className="font-semibold text-slate-900 mb-3">⚠️ What's Wrong?</h3>
            <textarea
              value={faultDescription}
              onChange={(e) => setFaultDescription(e.target.value)}
              placeholder="Describe the problem in detail: What's not working? When did it start? Any unusual sounds, smells, or leaks?"
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
            />
          </Card>
        )}

        {/* Replacement Unit Question */}
        {fromServiceCard && serviceDetails?.title?.toLowerCase().includes('replace') && (
          <Card>
            <h3 className="font-semibold text-slate-900 mb-3">🛒 Do you have a replacement unit?</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border border-slate-300 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  name="hasReplacementUnit"
                  checked={hasReplacementUnit === true}
                  onChange={() => setHasReplacementUnit(true)}
                  className="w-4 h-4 text-slate-900 focus:ring-slate-900"
                />
                <div>
                  <div className="font-medium text-slate-900">Yes, I have the new unit</div>
                  <div className="text-sm text-slate-600">Installation service only</div>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 border border-slate-300 rounded-lg hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  name="hasReplacementUnit"
                  checked={hasReplacementUnit === false}
                  onChange={() => setHasReplacementUnit(false)}
                  className="w-4 h-4 text-slate-900 focus:ring-slate-900"
                />
                <div>
                  <div className="font-medium text-slate-900">No, I need a new unit</div>
                  <div className="text-sm text-slate-600">Parts + installation service</div>
                </div>
              </label>
            </div>
          </Card>
        )}

        {/* Description */}
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-900">Describe the Issue</h3>
            <button
              type="button"
              disabled
              title="Not available yet"
              className="px-3 py-1.5 border border-slate-300 rounded-xl text-xs font-medium opacity-50 cursor-not-allowed"
            >
              🎙️ Voice
            </button>
          </div>
          <textarea 
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full border border-slate-300 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent" 
            rows={4} 
            placeholder="Please describe the issue in detail. For example: 'Kitchen sink has been leaking under the cabinet for 2 days. Water is pooling on the floor.'"
            required
          />
          <div className="flex gap-2 mt-3 flex-wrap">
            {['Plumbing', 'Electrical', 'HVAC', 'Urgent', ...ROOM_TAGS].map(tag => {
              const on = TRADE_TAGS[tag] ? trade === TRADE_TAGS[tag] : tag === 'Urgent' ? urgent : rooms.includes(tag)
              const toggle = () => {
                if (TRADE_TAGS[tag]) setTrade(on ? null : TRADE_TAGS[tag])
                else if (tag === 'Urgent') setUrgent(!urgent)
                else setRooms(on ? rooms.filter(r => r !== tag) : [...rooms, tag])
              }
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={toggle}
                  className={`px-3 py-1 text-xs border rounded-lg touch-manipulation ${on ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 hover:bg-slate-50'}`}
                >
                  {tag}
                </button>
              )
            })}
          </div>
        </Card>

        {/* Service address */}
        <Card>
          <h3 className="font-semibold text-slate-900 mb-3">Service Address</h3>
          {addresses.length === 0 ? (
            <div className="text-sm text-slate-600">
              No saved address. <button type="button" onClick={() => navigate('/addresses')} className="underline">Add one</button> so the professional knows where to come.
            </div>
          ) : (
            <select value={addressId} onChange={e => setAddressId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900">
              {addresses.map(a => (
                <option key={a.id} value={a.id}>{[a.label, a.address_line_1, a.city].filter(Boolean).join(' · ')}</option>
              ))}
            </select>
          )}
        </Card>

        {/* Submit Button */}
        <Card>
          <button 
            type="submit"
            disabled={loading || !description.trim()}
            className="w-full py-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting Request...
              </div>
            ) : (
              'Submit Service Request'
            )}
          </button>
        </Card>
      </form>
    </PageLayout>
  )
}
